import {Avatar, Box, Paper, Typography} from '@mui/material';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {CategorizedApplication} from '../../../../../backend/responses/categorized-application';
import Switch from '../../../../../components/switch/switch';
import '../../../../../components/paginated-table/paginated-table.scss';
import {translations} from '../../../../../i18n/translations';
import {avatarSizesAndCoefficients} from '../../../../../utils/constants/avatar-sizes-and-coefficients';

const copy = translations.family_applications;

// Family-only presentation: keep permission and unknown-state rules out of PaginatedTable.
export function ApplicationCatalogTable({
  rows,
  canManage,
  disabled,
  basePath,
  entityQuery,
  onToggle,
}: {
  rows: CategorizedApplication[];
  canManage: boolean;
  disabled: boolean;
  basePath: string;
  entityQuery: string;
  onToggle: (id: string) => void;
}) {
  const {t} = useTranslation();
  const permissionExplanation = t(translations.user_applications.disabled_state_tooltip);
  const actionLabel = `${t(copy.enable)} / ${t(copy.disable)}`;

  const avatarSize = avatarSizesAndCoefficients.avatarSizes.giant;
  const stateLabel = (active: boolean | undefined) =>
    active === true ? t(copy.enabled) : active === false ? t(copy.disabled) : '—';

  const headings = [
    t(translations.user_applications.name_header),
    t(translations.user_applications.category_header),
    ...(canManage ? [t(copy.access)] : []),
    t(translations.user_applications.state_value_true),
  ];

  return (
    <Box>
      <Paper
        className="paginated-table-line-container is-header"
        variant="outlined"
        sx={{backgroundColor: (theme) => theme.palette.action.hover}}
      >
        {headings.map((heading, index) => (
          <Box key={index} className="paginated-table-line-column">
            <Typography variant="body2" fontWeight="bold" sx={{flex: 1, textAlign: 'center'}}>
              {heading}
            </Typography>
          </Box>
        ))}
      </Paper>
      {rows.map(({application, category}) => (
        <Link
          key={application.id}
          className="paginated-table-row-link"
          to={`${basePath}/${application.id}${entityQuery}`}
        >
          <Paper
            className={`paginated-table-line-container is-row ${
              canManage && application.isActive === false ? 'is-inactive' : ''
            }`}
            sx={{'&:hover': {backgroundColor: (theme) => theme.palette.action.hover}}}
          >
            <Box className="paginated-table-line-column">
              <Avatar
                className="paginated-table-avatar"
                src={application.iconUrl || application.picture}
                alt={application.name || ''}
                sx={{
                  width: avatarSize,
                  height: avatarSize,
                  borderRadius: `${
                    avatarSize *
                    avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersHigh
                  }px`,
                  borderColor: (theme) => theme.palette.action.disabled,
                  backgroundColor: category?.systemButton?.enabledStateColor?.trim() || undefined,
                }}
              >
                <></>
              </Avatar>
              <Typography variant="body2" sx={{pl: '14px', overflowWrap: 'anywhere'}}>
                {application.name || '—'}
              </Typography>
            </Box>
            <Box className="paginated-table-line-column" sx={{justifyContent: 'center'}}>
              <Typography variant="body2">{category?.name || '—'}</Typography>
            </Box>
            {canManage && (
              <Box className="paginated-table-line-column" sx={{justifyContent: 'center'}}>
                <Typography variant="body2">{stateLabel(application.isActive)}</Typography>
              </Box>
            )}
            <Box
              className="paginated-table-line-column"
              sx={{justifyContent: 'center'}}
              onClick={(event) => {
                // Disabled controls and their permission tooltip must not open the detail page.
                event.stopPropagation();
                event.preventDefault();
              }}
            >
              {typeof application.isActive !== 'boolean' ? (
                <Typography variant="body2">—</Typography>
              ) : (
                <Switch
                  value={application.isActive}
                  disabled={!canManage || disabled}
                  disabledTooltip={!canManage ? permissionExplanation : undefined}
                  inputProps={{
                    'aria-label': canManage
                      ? actionLabel
                      : `${actionLabel}: ${permissionExplanation}`,
                  }}
                  onClick={() => {
                    if (canManage && !disabled && application.id) onToggle(application.id);
                  }}
                  sx={{mr: '12px'}}
                />
              )}
            </Box>
          </Paper>
        </Link>
      ))}
      {rows.length === 0 && (
        <Typography variant="body2" fontWeight="bold" textAlign="center" sx={{pt: 3}}>
          {t(translations.user_applications.no_applications)}
        </Typography>
      )}
    </Box>
  );
}
